import { toast } from "@/lib/toast";
import { apiClient } from "@/services/api";
import type { CreateWorkflowDTO, UpdateWorkflowDTO } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Query keys
export const workflowKeys = {
  all: ["workflows"] as const,
  lists: () => [...workflowKeys.all, "list"] as const,
  list: () => [...workflowKeys.lists()] as const,
  details: () => [...workflowKeys.all, "detail"] as const,
  detail: (id: number) => [...workflowKeys.details(), id] as const,
  executions: (workflowId: number) => [...workflowKeys.all, "executions", workflowId] as const,
  execution: (workflowId: number, executionId: number) =>
    [...workflowKeys.executions(workflowId), executionId] as const,
};

// Get all workflows
export function useWorkflows() {
  return useQuery({
    queryKey: workflowKeys.list(),
    queryFn: () => apiClient.getWorkflows(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Create workflow
export function useCreateWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateWorkflowDTO) => apiClient.createWorkflow(data),
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create workflow",
        variant: "destructive",
      });
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Workflow created successfully",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
    },
  });
}

// Update workflow
export function useUpdateWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateWorkflowDTO }) =>
      apiClient.updateWorkflow(id, data),
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update workflow",
        variant: "destructive",
      });
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Workflow updated successfully",
      });
    },
    onSettled: (_, __, { id }) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
    },
  });
}

// Delete workflow
export function useDeleteWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => apiClient.deleteWorkflow(id),
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Workflow deleted successfully",
      });

      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete workflow",
        variant: "destructive",
      });
    },
  });
}

// Duplicate workflow
export function useDuplicateWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => apiClient.duplicateWorkflow(id),
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Workflow duplicated successfully",
      });

      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to duplicate workflow",
        variant: "destructive",
      });
    },
  });
}

// Execute workflow
export function useExecuteWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, context }: { id: number; context: Record<string, unknown> }) =>
      apiClient.executeWorkflow(id, context),
    onSuccess: (_, { id }) => {
      toast({
        title: "Success",
        description: "Workflow execution started",
      });

      // Invalidate executions list
      queryClient.invalidateQueries({ queryKey: workflowKeys.executions(id) });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to execute workflow",
        variant: "destructive",
      });
    },
  });
}
